/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useServiceContext } from '../../../root'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'
import { createDateTimeFormatter } from '../../../utils/commonColumns'
import TableMenu from './TableMenu'
import type { Metadata } from '../../../services/api/metadata'
import { getNameByType } from '../../../services/api/metadata'

interface PropertiesTableProps {
  onEditProperty: (property: Metadata) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

const PropertiesTable: React.FC<PropertiesTableProps> = ({
  onEditProperty,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)

  const handleRowClick = (property: Metadata) => {
    onEditProperty(property)
  }

  return (
    <div>
      <RcmTable
        className="chita-table"
        noResultsText="NO_RESULTS_FILTERED"
        HeadRow={
          <TableHeadRow className="has-actions">
            <TableHeadCell style={{ fontSize: '12px' }} id="property-name-header" sort="NAME">
              {translate('NAME')}
            </TableHeadCell>
            <TableHeadCell style={{ fontSize: '12px' }} id="property-type-header" sort="TYPE">
              {translate('TYPE')}
            </TableHeadCell>
            <TableHeadCell style={{ fontSize: '12px' }} id="property-created-at-header" sort="CREATED_AT">
              {translate('CREATED_ON')}
            </TableHeadCell>
            <TableHeadCell style={{ fontSize: '12px' }} id="property-created-by-header" sort="CREATED_BY">
              {translate('CREATED_BY')}
            </TableHeadCell>
            <TableHeadCell style={{ fontSize: '12px' }} id="property-updated-at-header" sort="UPDATED_AT">
              {translate('TABLE_TH_MODIFIED_ON')}
            </TableHeadCell>
            <TableHeadCell style={{ fontSize: '12px' }} id="property-updated-by-header" sort="UPDATED_BY">
              {translate('TABLE_TH_MODIFIED_BY')}
            </TableHeadCell>
            <TableHeadCell style={{ fontSize: '12px' }} id="1ea525a2-c400-4a8a-8ace-f693240a13b5" className="actions">
              {translate('ACTIONS')}
            </TableHeadCell>
          </TableHeadRow>
        }
        Row={({ row }: { row: Metadata }) => {
          const property = row
          const propType = getNameByType(property.type, translate)

          return (
            <TableRow key={property.id}>
              <td className="clipped-cell">
                <div className="ellipsis">
                  <span className="btn-link" onClick={() => handleRowClick(property)} style={{ cursor: 'pointer' }}>
                    {property.name}
                  </span>
                </div>
              </td>
              <td>{propType ? propType.value : property.type}</td>
              <td>{formatDateTime(property.createdAt)}</td>
              <td>
                {property.createdBy && property.createdBy.name ? property.createdBy.name : translate('NOT_AVAILABLE')}
              </td>
              <td>{formatDateTime(property.updatedAt)}</td>
              <td>
                {property.updatedBy && property.updatedBy.name ? property.updatedBy.name : translate('NOT_AVAILABLE')}
              </td>
              <td className="actions">
                <TableMenu
                  property={property}
                  onEditProperty={onEditProperty}
                  onDeleteSuccess={onDeleteSuccess}
                  onExportStart={onExportStart}
                  onExportEnd={onExportEnd}
                />
              </td>
            </TableRow>
          )
        }}
      />
    </div>
  )
}

export default PropertiesTable
